import { DownloadSimpleIcon, LockSimpleIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { downloadJsonText } from '@/lib/readJsonFile';
import { Switch } from '@/components/ui/switch';
import type { WorkbookEditor } from './useWorkbookEditor';
import { WorkbookNamespaceField } from './WorkbookNamespaceField';
import { workbookDefinitionExport } from './workbookDefinitionExport';

export function WorkbookSettings({
  editor,
  canUpdate,
  busy,
}: {
  editor: WorkbookEditor;
  canUpdate: boolean;
  busy: boolean;
}) {
  const gt = useGT();
  return (
    <div className="max-w-2xl space-y-3">
      <h2 className="font-medium">
        <T context="workbook settings heading">Workbook settings</T>
      </h2>
      <section className="space-y-3 rounded-lg border p-3">
        <h3 className="text-sm font-medium">
          <T context="workbook general settings section">General</T>
        </h3>
        <label className="grid gap-2 text-sm">
          <T context="workbook settings name label">Name</T>
          <Input
            value={editor.definition.name}
            maxLength={200}
            disabled={!canUpdate || busy}
            aria-label={gt('Workbook name', { context: 'workbook name field' })}
            onChange={(event) =>
              editor.edits.workbook.update((definition) => ({
                ...definition,
                name: event.target.value,
              }))
            }
          />
        </label>
        <WorkbookNamespaceField
          workbookId={editor.workbookId}
          disabled={!canUpdate || busy}
          label={<T context="workbook settings namespace label">Namespace</T>}
        />
      </section>
      <section className="space-y-2 rounded-lg border p-3">
        <h3 className="text-sm font-medium">
          <T context="workbook process settings section">Process</T>
        </h3>
        <label className="flex items-start justify-between gap-3 text-sm">
          <span className="space-y-1">
            <span className="block">
              <T context="workbook sop auto apply label">
                Update instructions automatically
              </T>
            </span>
            <span className="block text-muted-foreground">
              <T context="workbook sop auto apply help">
                When settings change, the AI checks each instruction they affect
                and rewrites the ones that no longer match. Off, you review each
                rewrite first. On, rewrites apply as they arrive, and each one
                can be undone.
              </T>
            </span>
          </span>
          <Switch
            checked={editor.definition.sopAutoApply === true}
            disabled={!canUpdate || busy}
            aria-label={gt('Update instructions automatically', {
              context: 'workbook sop auto apply label',
            })}
            onCheckedChange={(checked) =>
              editor.edits.workbook.update((definition) => ({
                ...definition,
                sopAutoApply: checked,
              }))
            }
          />
        </label>
      </section>
      <section className="space-y-2 rounded-lg border p-3">
        <h3 className="text-sm font-medium">
          <T context="workbook sharing settings section">Sharing</T>
        </h3>
        <div className="flex items-center gap-2 text-sm">
          <LockSimpleIcon className="size-4 text-muted-foreground" />
          <T context="workbook private access status">Private</T>
        </div>
        <p className="text-sm text-muted-foreground">
          <T context="workbook current sharing availability">
            Only the owner can access this workbook. Sharing with teammates is
            not available yet.
          </T>
        </p>
      </section>
      <section className="space-y-2 rounded-lg border p-3">
        <h3 className="text-sm font-medium">
          <T context="workbook support settings section">Support</T>
        </h3>
        <p className="text-sm text-muted-foreground">
          <T context="workbook export definition help">
            Download this workbook's definition as JSON to send to support when
            something isn't working. It holds the workbook's setup, including
            any tables typed into it, but not its results.
          </T>
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            const { fileName, json } = workbookDefinitionExport(editor);
            downloadJsonText(fileName, json);
          }}
        >
          <DownloadSimpleIcon className="size-4" />
          <T context="workbook export definition action">Export Definition</T>
        </Button>
      </section>
    </div>
  );
}
